import { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '../../../../utils/api';

export const useSecurityData = (params, deviceInfo, preloadedData = null) => {
    const [sites, setSites] = useState(preloadedData?.sites || []);
    const [roles, setRoles] = useState(preloadedData?.roles || []);
    const [financialWeeks, setFinancialWeeks] = useState(preloadedData?.financialWeeks || []);
    const [securitySummary, setSecuritySummary] = useState(preloadedData?.securitySummary || []);
    const [loading, setLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dataFetched, setDataFetched] = useState(preloadedData !== null && preloadedData.sites?.length > 0);

    const fetchSites = useCallback(async () => {
        if (!deviceInfo) return;

        try {
            const res = await authenticatedFetch('/ManagerReport/GetSites', {
                method: 'GET',
            });

            if (!res.ok) {
                throw new Error('Failed to fetch sites');
            }

            const response = await res.json();
            setSites(response);
        } catch (err) {
            console.error('Error fetching sites:', err);
            setError(err.message);
        }
    }, [deviceInfo]);

    const fetchRoles = useCallback(async () => {
        if (!deviceInfo) return;

        try {
            const res = await authenticatedFetch('/ManagerReport/GetRolesForRoster', {
                method: 'GET',
            });

            if (!res.ok) {
                throw new Error('Failed to fetch roles');
            }

            const response = await res.json();

            if (response.message != null) {
                console.log(response);
            } else {
                setRoles(response.item1);
            }
        } catch (ex) {
            console.error('Error fetching roles:', ex);
            setError(ex.message);
        }
    }, [deviceInfo]);

    const fetchFinancialWeeks = useCallback(async () => {
        if (!deviceInfo) return;

        try {
            const res = await authenticatedFetch('/ManagerReport/GetFinancialWeeks', {
                method: 'GET',
            });

            if (!res.ok) {
                throw new Error('Failed to fetch financial weeks');
            }

            const response = await res.json();

            if (response.message != null) {
                console.log(response);
            } else {
                setFinancialWeeks(response.item1);
            }
        } catch (ex) {
            console.error('Error fetching financial weeks:', ex);
            setError(ex.message);
        }
    }, [deviceInfo]);

    const fetchSecuritySummary = useCallback(async () => {
        if (!deviceInfo) return;

        setSummaryLoading(true);
        try {
            const res = await authenticatedFetch('/ManagerReport/GetSecurityFulfillmentSummary', {
                method: 'GET',
            });

            if (!res.ok) {
                throw new Error('Failed to fetch security summary');
            }

            const response = await res.json();

            if (response.message != null) {
                console.log(response);
            } else {
                setSecuritySummary(response);
            }
        } catch (ex) {
            console.error('Error fetching security summary:', ex);
            setError(ex.message);
        } finally {
            setSummaryLoading(false);
        }
    }, [deviceInfo]);

    // Fetch once on mount; skip if preloaded data was provided
    useEffect(() => {
        if (dataFetched) return;

        const initialiseData = async () => {
            setLoading(true);
            setError(null);

            await Promise.all([
                fetchSites(),
                fetchRoles(),
                fetchFinancialWeeks(),
                fetchSecuritySummary()
            ]);

            setLoading(false);
            setDataFetched(true);
        };

        if (deviceInfo) {
            initialiseData();
        }
    }, [deviceInfo, dataFetched]);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        setError(null);

        await Promise.all([
            fetchSites(),
            fetchRoles(),
            fetchFinancialWeeks(),
            fetchSecuritySummary()
        ]);

        setLoading(false);
    }, [fetchSites, fetchRoles, fetchFinancialWeeks, fetchSecuritySummary]);

    return {
        sites,
        roles,
        financialWeeks,
        securitySummary,
        loading,
        summaryLoading,
        error,
        dataFetched,
        refreshAll,
        fetchSites,
        fetchRoles,
        fetchFinancialWeeks,
        fetchSecuritySummary
    };
};
